import { action, observable, ObservableMap, set as mobxSet } from "mobx";
import { DateTime } from "luxon";

import type { EventState, ShortGuid } from "@state/hubs/ScorePredictorHubStore";
import type {
	MatchPoints,
	SportsEvent,
	SportsEventPrediction,
} from "@api-types/score-predictor/ScorePredictorApiTypes";

const DEFAULT_LIVE_HOUR_WINDOW = 6;

class ScorePredictorLiveStateStore {
	public eventStates: ObservableMap<ShortGuid, EventState> = observable.map();
	public latestPoints: ObservableMap<ShortGuid, MatchPoints> =
		observable.map();

	@action.bound
	public mergeEventState(update: EventState | null | undefined) {
		if (!update?.eventId) {
			return;
		}

		const existing = this.eventStates.get(update.eventId);

		// Delta-merge: a null field means "keep the prior value", not "cleared".
		// isFinished is terminal — once true it stays true across later pushes.
		const score = update.score != null ? update.score : (existing?.score ?? null);
		const isFinished =
			update.isFinished === true || existing?.isFinished === true;
		const matchTime =
			update.matchTime != null
				? update.matchTime
				: (existing?.matchTime ?? null);

		const hasScore = score?.home != null && score?.away != null;

		// Nothing gradeable yet (no score and not final) → drop any stale entry.
		if (!isFinished && !hasScore) {
			this.eventStates.delete(update.eventId);
			this.latestPoints.delete(update.eventId);
			return;
		}

		this.eventStates.set(update.eventId, {
			eventId: update.eventId,
			score,
			matchTime: isFinished ? null : matchTime,
			isFinished,
		});

		// Points ride only the per-connection GetEventStates pull; the shared
		// EventLiveUpdate broadcast never carries them, so this is a no-op for pushes. On
		// resync it re-seeds the points overlay, closing the reconnect gap for a match that
		// settled while the client was disconnected (after which the live push goes silent).
		if (update.points != null) {
			this.latestPoints.set(update.eventId, update.points);
		}
	}

	@action.bound
	public setLatestPoints(eventId: ShortGuid, points: MatchPoints) {
		if (!eventId) {
			return;
		}
		this.latestPoints.set(eventId, points);
	}

	@action.bound
	public clearLatestPoints() {
		this.latestPoints.clear();
	}

	@action.bound
	public reset() {
		this.eventStates.clear();
		this.latestPoints.clear();
	}

	/** Filter events to those worth subscribing to live (started today or within
	 *  the last `hourWindow` hours, not already final). */
	public liveCandidateIds(
		events: SportsEvent[],
		hourWindow: number = DEFAULT_LIVE_HOUR_WINDOW
	): string[] {
		const now = DateTime.local();
		return events
			.filter((event) => {
				if (event.isFinished) {
					return false;
				}
				const start = DateTime.fromISO(event.startTime).toLocal();
				const isToday = start.hasSame(now, "day");
				const startedRecently =
					start <= now && start >= now.minus({ hours: hourWindow });
				return isToday || startedRecently;
			})
			.map((event) => event.sportsEventId)
			.filter(Boolean);
	}

	/** Fingerprint events by their current live state — used as a MobX reaction
	 *  key so the merge runs whenever any tracked field on any event changes. */
	public fingerprint(events: SportsEvent[]): string {
		if (!events.length) {
			return "";
		}
		return events
			.map((event) => {
				const id = event.sportsEventId;
				const state = this.eventStates.get(id);
				const s = state?.score;
				const pp = this.latestPoints.get(id);
				return `${id}:${s?.home ?? ""}-${s?.away ?? ""}:${state?.isFinished ? "F" : ""}:${pp ? `${pp.total}-${pp.status}-${JSON.stringify(pp.breakdown)}` : ""}`;
			})
			.sort()
			.join(",");
	}

	/** Merge live state into prediction rows: writes the live `score`/`isFinished` onto
	 *  each `sportsEvent`, and the projected/settled `points` overlay onto its `prediction`.
	 *  mobx.set keeps the writes reactive even when a field is absent on the REST response
	 *  (MobX 4 deep-observation only tracks properties present at wrap time). When eventStates
	 *  has no entry for an event, leaves its score alone — REST seeded the baseline, and the
	 *  hub's resync layer prunes stale entries before this runs. */
	@action.bound
	public mergeLiveState(
		rows: SportsEventPrediction[],
		leagueId?: number,
		options?: {
			mergeLatestPoints?: boolean;
		}
	) {
		if (!rows.length) {
			return;
		}

		const mergeLatestPoints = options?.mergeLatestPoints ?? true;

		for (const row of rows) {
			const id = row.sportsEvent.sportsEventId;
			const state = this.eventStates.get(id);

			if (state) {
				if (state.score?.home != null && state.score?.away != null) {
					mobxSet(row.sportsEvent, "score", {
						home: state.score.home,
						away: state.score.away,
					});
				}

				// isFinished is terminal — only advance false→true. Never revert a
				// REST-finished match because a stale in-play live entry lingers
				// (e.g. a missed/lagging "finished" push).
				if (state.isFinished) {
					mobxSet(row.sportsEvent, "isFinished", true);
				}
			}

			// The live points channel carries the authoritative latest value — projected
			// while the match is live, then the final settled value the moment the prediction
			// settles (EventPointsUpdate emits both, tagged by status). Never clobber a settled
			// total though: settled is terminal. Only the user's own prediction carries points.
			if (mergeLatestPoints && row.prediction) {
				const points = this.latestPoints.get(id);
				if (points != null && row.prediction.points?.status !== "Settled") {
					mobxSet(row.prediction, "points", points);
				}
			}
		}
	}

	/** True if the event has clock time or a final score (started or finished). */
	public hasStarted(matchId: string): boolean {
		const state = this.eventStates.get(matchId);
		return !!state && (state.matchTime != null || state.isFinished);
	}

	@action.bound
	public onDispose() {
		this.reset();
	}
}

export default ScorePredictorLiveStateStore;
